import { Controller } from "@hotwired/stimulus"

// Kontroler stworzony z myślą o izolacji struktury plików poza kontenerem
// np. nieznana zawartość emaila nie będzie korzystać / koegzystować ze strukturą aplikacji poza iframe

// Z założenia miejsce w kórym pojawia się iframe powinno być opakowane przez inny div
// tak, że iframe ma nieograniczoną szerokość i wysokość, a zewnętrzny div wtedy tworzy scrollbara
// przykład stylu iframe'a: .email_iframe w emails.css

// Zachowanie względem lazy w iframe: (_lazy_ - czy iframe ma być ładowany leniwie)
// - jeśli _lazy_ może być true, to potrzebujemy _src_ do renderowania
// - jeśli _lazy_ może być false, to można dodać _preload_ i bezpośrednio wstawić iframe do DOM (pomijamy żądanie http)
// - gdy _lazy_ jest zawsze true, to nie ma sensu dodawać _preload_
// - gdy _lazy_ jest zawsze false, to można się ograniczyć tylko do _preload_ i wtedy nie ma potrzeby dodawania _src_

export default class extends Controller {
  static values = { src: String, className: String, lazy: Boolean }
  static targets = ["preloadContent"]

  connect() {
    console.log("iframe controller connect")

    if (document.hidden && this.lazyValue) {
      // Gdy strona ładowana jest w dodatkowej karcie to frame z lazy jest pomijany
      this.addRenderAfterVisibilityChangeListener()
    } else {
      this.renderIframe()
    }
  }

  // usuwamy iframe, aby po powrocie do strony nie mieć starych i pustych iframe'ów
  disconnect() {
    const iframe = this.element.querySelector("iframe")
    if (iframe) {
      if (this.resizeObserver) this.resizeObserver.disconnect()
      iframe.remove()
    }
  }

  renderIframe() {
    const iframe = document.createElement("iframe")
    iframe.className = this.classNameValue
    iframe.style.visibility = "hidden"

    if (this.lazyValue) {
      iframe.setAttribute("loading", "lazy")
    }
    iframe.setAttribute("scrolling", "no")

    if (!this.lazyValue && this.hasPreloadContentTarget) {
      // korzystamy z preloadContent
      const preloadedContent = this.preloadedContentFromTarget()
      const blob = new Blob([preloadedContent], { type: "text/html" })
      const url = URL.createObjectURL(blob)
      iframe.onload = () => {
        URL.revokeObjectURL(url) // zwalniamy pamięć
        if (!this.resizeObserver) this.addAutoResizeListener(iframe) // fallback gdyby setupEarlyVisibility nie zadziałał
        this.resizeIframe(iframe) // finalny resize po załadowaniu wszystkich zasobów
      }
      iframe.src = url
    } else {
      // jest lazy lub nie ma preloadContent
      iframe.onload = () => {
        if (!this.resizeObserver) this.addAutoResizeListener(iframe) // fallback
        this.resizeIframe(iframe) // finalny resize po załadowaniu wszystkich zasobów
      }
      iframe.src = this.srcValue
    }

    this.element.appendChild(iframe)
    this.setupEarlyVisibility(iframe)
  }

  // Nie czekamy na załadowanie obrazków — pokazujemy iframe gdy DOM jest gotowy (readyState !== "loading")
  setupEarlyVisibility(iframe) {
    const check = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        // pomijamy about:blank — to wstępny dokument przed załadowaniem właściwej treści (lazy/HTTP path)
        if (doc && doc.readyState !== "loading" && !doc.URL.startsWith("about:")) {
          this.addAutoResizeListener(iframe)
          return
        }
      } catch (_e) { return } // cross-origin — onload fallback obsłuży
      requestAnimationFrame(check)
    }
    requestAnimationFrame(check)
  }

  // dopasowuje rozmiar iframe do zawartości, nie robimy tego poza iframe, przez wpływ elementów zewnętrznych
  // NOTE: - gdyby się okazało, że źle wylicza wielkość, może być kwestia, że nie zdąża uwględnić styli i obrazków
  //         wówczas warto rozważyć, dodanie nasłuchiwania na ich ładowanie wewnątrz iframe
  resizeIframe(iframe) {
    // guard — iframe może być już usunięty lub jeszcze nie gotowy
    if (!iframe.contentWindow?.document?.body) return

    // wymuszenie przeliczenia szerokości elementów na postawie wielkości kontenera
    // zapewnia to, dostosowanie automatycznych szerokości elementów wewnątrz iframe, do nowych warunków
    iframe.style.width = this.element.clientWidth + "px"

    // przeliczenie wymiarów na podstawie wygenerowanej zawartości
    // height: dodane no scrolling i bez documentElement - możliwe, że tak lepiej - aby zapobiec minium 150 px wysokości na przeglądarkach
    const doc = iframe.contentWindow.document
    const height = doc.body.scrollHeight
    const width = Math.max(doc.body.scrollWidth, doc.documentElement.scrollWidth)

    if (iframe.style.height !== height + "px") { iframe.style.height = height + "px" }
    if (iframe.style.width !== width + "px") { iframe.style.width = width + "px" }
    iframe.style.visibility = "visible"
    // console.log(`iframe resized to ${width}x${height} ${this.isLoaded(iframe) ? "loaded" : "not loaded"}`)
  }

  preloadedContentFromTarget() {
    const content = this.preloadContentTarget.innerHTML
    const stylesheets = Array.from(document.querySelectorAll("link[rel=\"stylesheet\"]"))
      .map(link => `<link rel="stylesheet" href="${link.href}">`)
      .join("\n")
    const baseHref = `<base href="${window.location.origin}">`

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          ${baseHref}
          ${stylesheets}
        </head>
        <body>
          ${content}
        </body>
      </html>
    `
  }

  // reakcja na natywne zdarzene visibilitychange przeglądarki
  addRenderAfterVisibilityChangeListener() {
    this.visibilityHandler = () => {
      if (!document.hidden) {
        this.renderIframe()
        document.removeEventListener("visibilitychange", this.visibilityHandler)
      }
    }
    document.addEventListener("visibilitychange", this.visibilityHandler)
  }

  // reakcja na zmianę wielkości elementu - aktualizacja rozmiaru iframe
  // głównie przydaje się w kwestii wysokości, jak pojawia się element na widoku (z niewidzocznego obszaru)
  addAutoResizeListener(iframe) {
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        this.resizeIframe(iframe)
      })
    })
    this.resizeObserver.observe(this.element)

    // po załadowaniu każdego obrazka, przelicz rozmiar iframe
    // (obrazki mogą się ładować po ustawieniu visibility, zmieniając wysokość contentu)
    this.addImageLoadListeners(iframe)
  }

  // nasłuchiwanie na załadowanie obrazków wewnątrz iframe, po każdym przelicza rozmiar
  addImageLoadListeners(iframe) {
    try {
      const imgs = iframe.contentDocument?.querySelectorAll("img")
      if (!imgs) return

      imgs.forEach(img => {
        if (!img.complete) {
          img.addEventListener("load", () => this.resizeIframe(iframe), { once: true })
        }
      })
    } catch (_e) { /* cross-origin — pomijamy */ }
  }

  // może rzucić wyjątek - CORS - ale my używamy tylko naszej domeny, więc ok
  isLoaded(iframe) {
    return iframe && iframe.contentWindow && iframe.contentWindow.document.readyState === "complete"
  }
}
